<script lang="ts">
  // One account menu for every role (wiki/feature-access-model.md §6); the
  // impersonation chrome is wiki/feature-impersonation.md AC13.
  import { tick } from 'svelte';
  import { page } from '$app/stores';
  import { resolve } from '$app/paths';
  import { auth, isPremium, logout, persistCulture } from '$lib/stores/auth';
  import { creditBalance } from '$lib/stores/creditBalance.svelte';
  import { formatCredits } from '$lib/utils/formatting/credits';
  import { impersonationStop } from '$lib/stores/impersonationStop.svelte';
  import { loginHref } from '$lib/utils/routing/loginRedirect';
  import { isAdminHost } from '$lib/utils/routing/navRoutes';
  import { quotaStore } from '$lib/stores/quotaStore';
  import { displayName } from '$lib/utils/formatting/displayName';
  import { APP_VERSION } from '$lib/api/version';
  import { AnimatedDots, Badge, CultureToggle, ThemeToggle, type BadgeTone } from '$lib/ui';
  import AvatarBadge from '$lib/ui/profile/AvatarBadge.svelte';
  import VersionDialog from '$lib/ui/nav/VersionDialog.svelte';
  import { t } from '$lib/i18n';
  import { BillingKeys, UiKeys } from '@voicedojo/backend-types';

  interface Props {
    variant?: 'inline' | 'block';
  }

  let { variant = 'inline' }: Props = $props();

  // `$auth.user` is always the account being ACTED AS.
  const user = $derived($auth.status === 'authenticated' ? $auth.user : null);
  const impersonation = $derived(user?.impersonation ?? null);

  async function onStopImpersonating(): Promise<void> {
    if (!impersonation || $auth.status !== 'authenticated') return;
    await impersonationStop.run(impersonation.adminUserId, $auth.user.id);
  }

  const quota = quotaStore;

  let open = $state(false);
  let showAbout = $state(false);
  let triggerEl: HTMLButtonElement | null = $state(null);
  let rootEl: HTMLDivElement | null = $state(null);

  function greetingFor(u: { name?: string | null; email: string }): string {
    const name = u.name?.trim();
    if (name) return name.split(/\s+/)[0];
    const local = u.email.split('@')[0] ?? u.email;
    return local.length === 0 ? local : local[0].toUpperCase() + local.slice(1);
  }

  // Only Premium/Admin earn a badge on a signed-in trigger.
  const roleBadge = $derived.by((): { text: string; tone: BadgeTone } | null => {
    if (!user) return null;
    if ($isPremium) return { text: t(UiKeys.Account.BadgePremium), tone: 'info' };
    if (user.roles.includes('Admin')) return { text: t(UiKeys.Account.BadgeAdmin), tone: 'info' };
    return null;
  });

  async function toggle(): Promise<void> {
    open = !open;
    if (open) {
      await tick();
      rootEl?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }

  function close(returnFocus = true): void {
    if (!open) return;
    open = false;
    if (returnFocus) triggerEl?.focus();
  }

  async function onLogout(): Promise<void> {
    close(false);
    await logout();
  }

  function onAbout(): void {
    close(false);
    showAbout = true;
  }

  $effect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    }
    function onPointer(e: PointerEvent): void {
      if (rootEl && !rootEl.contains(e.target as Node)) close(false);
    }
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onPointer, true);
    };
  });
</script>

{#snippet practicesLeft(remaining: number)}
  {remaining === 1
    ? t(UiKeys.Account.PracticesLeftOne, { Remaining: remaining })
    : t(UiKeys.Account.PracticesLeftMany, { Remaining: remaining })}
{/snippet}

<div class="account-menu" class:block={variant === 'block'} bind:this={rootEl}>
  {#if $auth.status === 'loading'}
    <!-- Until whoami settles: no trigger, so a returning user never flashes the guest chrome. -->
    {#if $quota.quota}
      <span
        class="anon-balance"
        aria-label={t(UiKeys.Account.DailyPracticesLeftAria)}
        title={t(UiKeys.Account.PracticesLeftDetail, { Remaining: $quota.quota.remaining, Limit: $quota.quota.limit })}
      >
        {@render practicesLeft($quota.quota.remaining)}
      </span>
    {/if}
  {:else}
    <button
      type="button"
      class="trigger"
      bind:this={triggerEl}
      onclick={toggle}
      aria-haspopup="menu"
      aria-expanded={open}
      data-testid="account-trigger"
    >
      {#if !user}
        <span data-testid="guest-badge"><Badge tone="neutral">{t(UiKeys.Account.BadgeGuest)}</Badge></span>
        <span class="affordance" aria-hidden="true"><span class="caret">⌄</span></span>
      {:else if impersonation}
        <!-- AC13.4: no avatar — one identity's avatar beside another's name is the confusion. -->
        <span class="hello">{displayName(user)}</span>
        <Badge tone="info">{t(UiKeys.Impersonation.Badge)}</Badge>
        <span class="affordance" aria-hidden="true"><span class="caret">⌄</span></span>
      {:else}
        <span class="hello">{t(UiKeys.Account.Greeting, { Name: greetingFor(user) })}</span>
        {#if roleBadge}<Badge tone={roleBadge.tone}>{roleBadge.text}</Badge>{/if}
        <span class="affordance" aria-hidden="true">
          <AvatarBadge avatar={user.avatar} userId={user.id} size={1.5} />
          <span class="caret">⌄</span>
        </span>
      {/if}
    </button>
    {#if open}
      <div class="panel" role="menu" aria-label={t(UiKeys.Account.MenuAria)}>
        {#if user && impersonation}
          <div class="identity" aria-label={t(UiKeys.Impersonation.IdentityAria)}>
            <p class="identity-row">{t(UiKeys.Impersonation.SignedInAs, { Email: impersonation.adminEmail })}</p>
            <p class="identity-row">
              {t(UiKeys.Impersonation.ActingAsDetail, { Name: displayName(user), Email: user.email })}
            </p>
            <p class="identity-row">{t(UiKeys.Impersonation.Reason, { Reason: impersonation.reason })}</p>
          </div>
          <button
            type="button"
            class="item stop-impersonating"
            role="menuitem"
            onclick={onStopImpersonating}
            disabled={impersonationStop.busy}
          >
            {impersonationStop.busy ? t(UiKeys.Impersonation.StopStopping) : t(UiKeys.Impersonation.StopIdle)}
          </button>
          {#if impersonationStop.error}
            <p class="stop-error" role="status">{impersonationStop.error}</p>
          {/if}
        {/if}
        {#if user || $quota.quota}
          <div class="balances" aria-label={t(UiKeys.Account.BalancesAria)}>
            {#if user}
              <!-- The spendable total: the backend charges the paid pool and the monthly allowance alike. -->
              <a
                class="balance-row balance-link"
                role="menuitem"
                href={resolve('/billing')}
                title={t(UiKeys.Account.SpendableBreakdown, {
                  Paid: formatCredits(creditBalance.paid ?? 0),
                  Monthly: creditBalance.monthly ?? 0,
                })}
                onclick={() => close(false)}
              >
                {#if user.creditAccountPendingSince}
                  {t(UiKeys.Pending.CreditsSettingUp)}<AnimatedDots />
                {:else if creditBalance.total === null}
                  <AnimatedDots />
                {:else}
                  <span class="balance-value" class:balance-debt={creditBalance.total < 0}>{formatCredits(creditBalance.total)}</span>
                  {t(UiKeys.Account.AiCredits)}
                {/if}
              </a>
              {#if creditBalance.total !== null && creditBalance.total < 0}
                <a
                  class="balance-row balance-link balance-debt"
                  role="menuitem"
                  href={resolve('/pricing')}
                  data-testid="account-credit-debt"
                  onclick={() => close(false)}
                >
                  {t(BillingKeys.Credits.InDebt, { 0: -creditBalance.total })}
                </a>
              {/if}
            {/if}
            {#if $quota.quota}
              <p
                class="balance-row"
                title={t(UiKeys.Account.PracticesLeftDetail, { Remaining: $quota.quota.remaining, Limit: $quota.quota.limit })}
              >
                {@render practicesLeft($quota.quota.remaining)}
              </p>
            {/if}
          </div>
        {/if}
        {#if user}
          <a class="item" role="menuitem" href={resolve('/reviews')} onclick={() => close(false)}>{t(UiKeys.Account.Reviews)}</a>
          <a class="item" role="menuitem" href={resolve('/profile')} onclick={() => close(false)}>{t(UiKeys.Account.Profile)}</a>
          <a class="item" role="menuitem" href={resolve('/billing')} onclick={() => close(false)}>{t(UiKeys.Account.Subscription)}</a>
          {#if !impersonation && user.roles.includes('Admin') && isAdminHost($page.url.hostname)}
            <a class="item" role="menuitem" href={resolve('/admin')} onclick={() => close(false)} data-testid="admin-console-link">{t(UiKeys.Account.AdminConsole)}</a>
          {/if}
        {:else}
          <a
            class="item signin"
            role="menuitem"
            href={resolve(loginHref($page.url.pathname + $page.url.search))}
            onclick={() => close(false)}
            data-testid="guest-signin">{t(UiKeys.Account.SignIn)}</a
          >
        {/if}
        <a class="item" role="menuitem" href={resolve('/support')} onclick={() => close(false)} data-testid="support-link"
          >{t(UiKeys.Footer.Support)}</a
        >
        {#if user}
          <button type="button" class="item signout" role="menuitem" onclick={onLogout}>{t(UiKeys.Account.SignOut)}</button>
        {/if}
        <div class="prefs" role="group" aria-label={t(UiKeys.Account.Preferences)}>
          <div class="pref-row"><span>{t(UiKeys.Account.Language)}</span><CultureToggle onswitch={persistCulture} /></div>
          <div class="pref-row"><span>{t(UiKeys.Account.Theme)}</span><ThemeToggle /></div>
        </div>
        <button type="button" class="item version" role="menuitem" title={t(UiKeys.Layout.AboutLabel)} onclick={onAbout}>
          {t(UiKeys.Account.Version, { Version: APP_VERSION })}
        </button>
      </div>
    {/if}
  {/if}
</div>

<VersionDialog open={showAbout} onClose={() => (showAbout = false)} />

<style>
  /* `min-width: 0` down to `.hello`: a user-set name must ellipsize, not scroll the page. */
  .account-menu {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .account-menu.block {
    display: block;
  }

  .trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: none;
    border: none;
    cursor: pointer;
    font: inherit;
    color: var(--ink);
    padding: 0.3rem 0.5rem;
    border-radius: 6px;
    min-width: 0;
  }

  .trigger > :global(.vd-badge) {
    flex-shrink: 0;
  }

  .trigger:hover,
  .trigger:focus-visible {
    background: color-mix(in srgb, var(--accent) 8%, transparent);
  }

  .account-menu.block .trigger {
    width: 100%;
    justify-content: flex-start;
  }

  .hello {
    font-size: var(--text-body);
    color: var(--ink);
    font-weight: 700;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .affordance {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    color: var(--ink-soft);
  }

  .trigger:hover .affordance,
  .trigger:focus-visible .affordance {
    color: var(--ink);
  }

  .caret {
    font-size: 0.7rem;
    color: inherit;
    line-height: 1;
    transition: transform 0.15s ease;
  }

  .trigger[aria-expanded='true'] .caret {
    transform: rotate(180deg);
  }

  .panel {
    position: absolute;
    top: calc(100% + 0.35rem);
    right: 0;
    z-index: 50;
    display: flex;
    flex-direction: column;
    min-width: 13rem;
    padding: 0.35rem;
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgb(0 0 0 / 0.14);
  }

  .account-menu.block .panel {
    position: static;
    box-shadow: none;
    border: none;
    padding: 0.25rem 0 0;
    min-width: 0;
  }

  .identity {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    padding: 0.35rem 0.6rem 0.5rem;
    border-bottom: 1px solid var(--line);
  }

  .identity-row {
    margin: 0;
    font-size: var(--text-meta);
    color: var(--ink-mute);
    line-height: 1.4;
    overflow-wrap: anywhere;
  }

  .item.stop-impersonating {
    color: var(--color-marigold-text);
    font-weight: 700;
  }

  .item.stop-impersonating:disabled {
    cursor: default;
    opacity: 0.6;
  }

  .stop-error {
    margin: 0;
    padding: 0 0.6rem 0.35rem;
    font-size: var(--text-meta);
    color: var(--color-recording-text);
  }

  .balances {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    padding: 0.35rem 0.6rem 0.5rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid var(--line);
  }

  .balance-row {
    margin: 0;
    font-size: var(--text-meta);
    color: var(--ink-mute);
    line-height: 1.4;
  }

  /* The negative margin lets the hover wash bleed to the region's edge while the text stays aligned. */
  .balance-link {
    display: block;
    text-decoration: none;
    cursor: pointer;
    margin: -0.1rem -0.35rem 0;
    padding: 0.1rem 0.35rem;
    min-height: 24px;
    border-radius: 6px;
  }

  .balance-link:hover,
  .balance-link:focus-visible {
    background: color-mix(in srgb, var(--accent) 8%, transparent);
  }

  .balance-value {
    font-weight: 700;
    color: var(--ink-soft);
  }

  .balance-debt,
  .balance-value.balance-debt {
    color: var(--color-error-text);
  }

  .anon-balance {
    font-size: var(--text-meta);
    color: var(--ink-mute);
    white-space: nowrap;
  }

  .item {
    display: block;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
    color: var(--ink);
    text-decoration: none;
    font: inherit;
    font-size: var(--text-body);
    font-weight: 400;
    padding: 0.5rem 0.6rem;
    border: none;
    background: none;
    border-radius: 6px;
    cursor: pointer;
  }

  .item:hover,
  .item:focus-visible {
    background: color-mix(in srgb, var(--accent) 8%, transparent);
  }

  .item.signin {
    color: var(--accent-text);
    font-weight: 700;
  }

  .item.signout {
    color: var(--color-recording-text);
  }

  .item.signout:hover,
  .item.signout:focus-visible {
    background: color-mix(in srgb, var(--color-recording) 10%, transparent);
  }

  .prefs {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.25rem;
    padding: 0.5rem 0.6rem 0.25rem;
    border-top: 1px solid var(--line);
  }

  .pref-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    font-size: var(--text-meta);
    color: var(--ink-soft);
  }

  .item.version {
    font-size: var(--text-meta);
    color: var(--ink-mute);
    padding-block: 0.35rem;
  }
</style>
